import { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { formatDate, formatMetric, formatNumber } from '../../../../utils/util';
import { ACCEPTED, CONCLUDED, EXPRESSION_OF_INTEREST, PENDING, REJECTED } from '../../../../utils/constants';

import DatePicker from '../../../common/inputs/date-picker/DatePicker';
import Dropdown from '../../../common/inputs/dropdown/Dropdown';
import TextInput from '../../../common/inputs/text-input/TextInput';
import Button from '../../../common/button/Button';

import { ReactComponent as SearchIcon } from '../../../../resources/icons/search.svg';

import './deal-room.scss';

const DealRoom = () => {
  const [fromDate, setFromDate] = useState(undefined);
  const [toDate, setToDate] = useState(undefined);
  const [status, setStatus] = useState(undefined);
  const [keywords, setKeywords] = useState('');
  const [filteredContracts, setFilteredContracts] = useState([]);

  const navigate = useNavigate();

  const { contracts } = useSelector(({ contract }) => contract);

  const statusOptions = useRef([
    { label: 'Expression Of Interest', value: EXPRESSION_OF_INTEREST },
    { label: 'Pending', value: PENDING },
    { label: 'Accepted', value: ACCEPTED },
    { label: 'Concluded', value: CONCLUDED },
    { label: 'Rejected', value: REJECTED },
  ]);

  useEffect(() => {
    let filteredContracts = [...contracts];
    if (status) {
      filteredContracts = filteredContracts.filter((contract) => contract.status.toLowerCase() === status.value);
    }

    if (fromDate) {
      filteredContracts = filteredContracts.filter((contract) => fromDate <= new Date(contract.dateInitiated));
    }

    if (toDate) {
      filteredContracts = filteredContracts.filter((contract) => toDate >= new Date(contract.dateInitiated));
    }

    if (keywords) {
      filteredContracts = filteredContracts.filter((contract) =>
        contract.product.toLowerCase().includes(keywords.toLowerCase())
      );
    }

    setFilteredContracts(filteredContracts);
  }, [contracts, fromDate, toDate, status, keywords]);

  const resetFilters = () => {
    setStatus(undefined);
    setKeywords('');
    setFromDate(undefined);
    setToDate(undefined);
  };

  const renderTable = () => (
    <table className="deal-room__table">
      <thead className="deal-room__table__header">
        <tr>
          <th>Deal</th>
          <th>Status</th>
          <th>Product</th>
          <th>Volume</th>
          <th>Price</th>
          <th>Date Initiated</th>
          <th>Last Active Date</th>
        </tr>
      </thead>
      <tbody className="deal-room__table__body">
        {filteredContracts.map((contract, index) => {
          const {
            dealNumber,
            status,
            product,
            volume,
            productUnit,
            pricePerProductUnit,
            metricUnit,
            dateInitiated,
            lastActiveDate,
          } = contract;

          const price = pricePerProductUnit * (volume / productUnit);

          return (
            <tr key={index} onClick={() => navigate(`/shop/deal-room/${contract.externalReference}`)}>
              <td>#{dealNumber}</td>
              <td>{status}</td>
              <td>{product}</td>
              <td>
                {formatNumber(volume)} {formatMetric(metricUnit, volume)}
              </td>
              <td>USD {formatNumber(price)}</td>
              <td>{formatDate(dateInitiated)}</td>
              <td>{formatDate(lastActiveDate)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <section className="deal-room" data-testid="deal-room">
      <section className="deal-room__filter">
        <h1 className="deal-room__title">Deal Room</h1>
        <h2 className="deal-room__subtitle">Filter</h2>
        <section className="deal-room__filters-container">
          <section className="deal-room__filters">
            <DatePicker name="from-date" placeholder="From" value={fromDate} onChange={(date) => setFromDate(date)} />
            <DatePicker name="from-date" placeholder="To" value={toDate} onChange={(date) => setToDate(date)} />
            <Dropdown
              name="status"
              placeholder="Status"
              value={status}
              options={statusOptions.current}
              onChange={(option) => setStatus(option)}
            />
            <TextInput
              name="keywords"
              placeholder="Keywords"
              value={keywords}
              onChange={({ target }) => setKeywords(target.value)}
              icon={<SearchIcon className="deal-room__search-icon" />}
            />
          </section>
          <Button className="deal-room__reset-filter-button" onClick={resetFilters}>
            Reset Filters
          </Button>
        </section>
      </section>

      <section className="deal-room__table-container">
        {filteredContracts.length === 0 ? <p>No Deals Found</p> : renderTable()}
      </section>
    </section>
  );
};

export default DealRoom;
